// src/components/Invoices.js
import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import "../styles/Invoices.css";
import {
  FaSearch,
  FaDownload,
  FaTrashAlt,
  FaFilter,
  FaRegCalendarAlt,
  FaPaperPlane,
  FaPlus,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { getSocket } from "../lib/socket";
import FilterInvoicesModal from "./FilterInvoicesModal";
import { useTranslation } from "react-i18next";
import Select from "react-select";
/* === Pretty PDF (print dialog) === */
import { useReactToPrint } from "react-to-print";
import InvoicePrint from "./InvoicePrint";
import { selectClientInfo } from "../lib/selectClientInfo";
import PurchaseOrders from "./PurchaseOrders";
import { readInvoicesAuxCache, writeInvoicesAuxCache } from "../lib/invoicesAuxCache";

const PO_CACHE_KEY = "purchase_orders_cache_v1";

/* ===================== Helpers ===================== */
const formatDateDots = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d)) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}.${mm}.${yy}`;
};

const dateWithin = (dateStr, from, to) => {
  if (!from && !to) return true;
  if (!dateStr || dateStr === "—") return false;

  const v = new Date(dateStr).setHours(0, 0, 0, 0);
  if (Number.isNaN(v)) return false;

  if (from) {
    const fromValue = new Date(from).setHours(0, 0, 0, 0);
    if (!Number.isNaN(fromValue) && v < fromValue) return false;
  }

  if (to) {
    const toValue = new Date(to).setHours(0, 0, 0, 0);
    if (!Number.isNaN(toValue) && v > toValue) return false;
  }

  return true;
};

const parseMoneyValue = (value) => {
  if (value == null || value === "—") return null;

  const raw = String(value)
    .trim()
    .replace(/[^\d.,-]/g, "");

  if (!raw) return null;

  // Ex: "1.234,56" -> "1234.56"
  if (raw.includes(",") && raw.includes(".")) {
    return Number(raw.replace(/\./g, "").replace(",", "."));
  }

  // Ex: "1234,56" -> "1234.56"
  if (raw.includes(",")) {
    return Number(raw.replace(",", "."));
  }

  return Number(raw);
};

const toCSV = (rows) => {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    const s = (v ?? "").toString();
    const needs = /[",\n;]|\s$|^\s/.test(s);
    const e = s.replace(/"/g, '""');
    return needs ? `"${e}"` : e;
  };
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");
};

const download = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// transforma path relativo (ex: "/uploads/xyz.png") em absoluto com baseURL do axios
const absoluteFromApiBase = (p) => {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  const base = (axios.defaults?.baseURL || "").replace(/\/$/, "");
  return base ? `${base}${p.startsWith("/") ? "" : "/"}${p}` : p;
};

// datas em ISO seguro
function safeISO(dateLike) {
  if (!dateLike) return undefined;
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return undefined;
  return d.toISOString();
}

// primeiro valor não vazio
const firstNonEmpty = (...cands) =>
  cands.find((v) => v != null && String(v).trim() !== "") ?? "";

const tryShowPicker = (target) => {
  try {
    target?.showPicker?.();
    target?.focus?.();
  } catch (_) {
    // ignore (browser user gesture rules)
  }
};

const getInvoiceStatusTheme = (status) => {
  switch (String(status || "DRAFT").toUpperCase()) {
    case "SENT":
      return {
        bg: "rgba(59, 130, 246, 0.16)",
        border: "rgba(59, 130, 246, 0.42)",
        color: "#bfdbfe",
      };
    case "PARTIAL":
      return {
        bg: "rgba(245, 158, 11, 0.16)",
        border: "rgba(245, 158, 11, 0.42)",
        color: "#fde68a",
      };
    case "PAID":
      return {
        bg: "rgba(34, 197, 94, 0.16)",
        border: "rgba(34, 197, 94, 0.42)",
        color: "#bbf7d0",
      };
    case "VOID":
      return {
        bg: "rgba(239, 68, 68, 0.16)",
        border: "rgba(239, 68, 68, 0.42)",
        color: "#fecaca",
      };
    case "DRAFT":
    default:
      return {
        bg: "rgba(100, 116, 139, 0.18)",
        border: "rgba(148, 163, 184, 0.35)",
        color: "#dbe4ee",
      };
  }
};

const getInvoiceStatusOptions = (t) => [
  { value: "DRAFT", label: t("status.draft") },
  { value: "SENT", label: t("status.sent") },
  { value: "PARTIAL", label: t("status.partial") },
  { value: "PAID", label: t("status.paid") },
  { value: "VOID", label: t("status.void") },
];

const buildInvoiceStatusSelectStyles = (status) => {
  const theme = getInvoiceStatusTheme(status);

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: theme.bg,
      borderColor: state.isFocused ? "#4953ff" : theme.border,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(73,83,255,0.15)" : "none",
      cursor: "pointer",
      "&:hover": {
        borderColor: state.isFocused ? "#4953ff" : theme.border,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: 34,
      padding: "0 8px 0 10px",
    }),
    singleValue: (base) => ({
      ...base,
      color: theme.color,
      fontSize: 13,
      fontWeight: 600,
    }),
    input: (base) => ({
      ...base,
      color: theme.color,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: theme.color,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#cbd5e1",
      padding: 6,
      "&:hover": {
        color: "#ffffff",
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#0f1318",
      border: "1px solid #2a3547",
      borderRadius: 10,
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      padding: 6,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      borderRadius: 8,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#2563eb"
        : state.isFocused
        ? "rgba(255,255,255,0.06)"
        : "transparent",
      color: "#e5e7eb",
    }),
  };
};

/* ===================== Export Modal (global) ===================== */

function ExportInvoicesModal({
  open,
  onClose,
  onConfirm,
  defaultFrom = "",
  defaultTo = "",
  count = 0,
}) {
  const { t } = useTranslation("invoices");

  // helper com fallback (para não rebentar se faltar alguma key)
  const T = (k, fallback, opts) =>
    t(k, { defaultValue: fallback, ...(opts || {}) });

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [format, setFormat] = useState("csv"); // csv | xls | pdf
  
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFrom(defaultFrom || "");
      setTo(defaultTo || "");
      setFormat("csv");
    }
  }, [open, defaultFrom, defaultTo]);

  // ESC para fechar (igual ao PO)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // labels com fallback para as tuas keys atuais (date_of/date_until)
  const labelFrom = T("export.date_from", T("export.date_of", "Date from"));
  const labelTo = T("export.date_to", T("export.date_until", "Date to"));

  return (
    <div className="export-modal-backdrop" onMouseDown={onClose}>
      <div
        className="invoices-modal invoices-export-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="invoices-modal__header">
          <h3 className="invoices-modal__title">
            {T("export.title", "Export Invoices")}
          </h3>

          <button
            type="button"
            className="invoices-modal__close"
            onClick={onClose}
            aria-label={T("common.close", "Close")}
            title={T("common.close", "Close")}
          >
            ✕
          </button>
        </div>

        <div className="invoices-modal__body">
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
            {T("export.scope", "Rows to export")}: <strong>{count ?? 0}</strong>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 6,
            }}
          >
            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85 }}>
                {labelFrom}
              </label>

              <div style={{ position: "relative" }}>
                <input
                  ref={fromInputRef}
                  type="date"
                  className="load-history-input"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  onPointerDown={(e) => tryShowPicker(e.currentTarget)}
                  style={{ width: "100%", paddingRight: 32 }}
                />

                <FaRegCalendarAlt
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(fromInputRef.current);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(fromInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>

              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                {from ? formatDateDots(from) : ""}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85 }}>
                {labelTo}
              </label>

              <div style={{ position: "relative" }}>
                <input
                  ref={toInputRef}
                  type="date"
                  className="load-history-input"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  onPointerDown={(e) => tryShowPicker(e.currentTarget)}
                  style={{ width: "100%", paddingRight: 32 }}
                />

                <FaRegCalendarAlt
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(toInputRef.current);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(toInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>

              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                {to ? formatDateDots(to) : ""}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
              {T("export.format", "Format")}
            </div>

            <div className="invoices-modal__options">
              {[
                { value: "csv", label: T("export.formats.csv", "CSV") },
                { value: "xls", label: T("export.formats.xls", "Excel (XLSX)") },
                { value: "pdf", label: T("export.formats.pdf", "PDF") },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`invoices-modal__option ${
                    format === opt.value ? "is-active" : ""
                  }`}
                  onClick={() => setFormat(opt.value)}
                >
                  <span>{opt.label}</span>
                  {format === opt.value && (
                    <span className="invoices-modal__check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="invoices-modal__footer">
          <button className="load-history-btn" onClick={onClose} type="button">
            {T("common.cancel", "Cancel")}
          </button>

          <button
            className="load-history-btn load-history-btn-primary"
            onClick={() => onConfirm({ from, to, format })}
            disabled={!count}
            style={!count ? { opacity: 0.6, pointerEvents: "none" } : undefined}
            type="button"
          >
            {T("export.export", "Export")}
          </button>
        </div>
      </div>
    </div>
  );
}

// URL view helper (must be outside component to avoid TDZ in useState init)
const viewFromUrl = (search) => {
  const v = new URLSearchParams(search || "").get("view");
  return v === "purchase_orders" ? "purchase_orders" : "invoices";
};

// cache key (fora do componente para ser usado pelos helpers)
const INVOICES_CACHE_KEY = "invoices_summary_cache_v1";

function persistInvoicesCache({ items, total, nextCursor, hasMore }) {
  try {
    localStorage.setItem(
      INVOICES_CACHE_KEY,
      JSON.stringify({
        ts: Date.now(),
        items: items || [],
        total: typeof total === "number" ? total : 0,
        nextCursor: nextCursor ?? null,
        hasMore: !!hasMore,
      })
    );
  } catch {}
}

function readInvoicesCache() {
  try {
    const raw = localStorage.getItem(INVOICES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return null;

    return {
      items: parsed.items,
      total: typeof parsed.total === "number" ? parsed.total : 0,
      nextCursor: parsed.nextCursor ?? null,
      hasMore: !!parsed.hasMore,
    };
  } catch {
    return null;
  }
}

/* ===================== Componente principal ===================== */
export default function Invoices() {
  const { t } = useTranslation(["invoices", "purchase_orders"]);

  const navigate = useNavigate();
  const location = useLocation();

  /* ===================== Toggle: Invoices / Purchase Orders ===================== */
  const [selectedDocView, setSelectedDocView] = useState(() =>
    viewFromUrl(location.search)
  );
  const isPO = selectedDocView === "purchase_orders";

  const tPO = (key, fallback, opts) =>
    t(key, { ns: "purchase_orders", defaultValue: fallback, ...(opts || {}) });
  
  // ✅ 1) ler cache 1x (antes do 1º paint)
  const [cacheInit] = useState(() => readInvoicesCache());

  // ✅ 2) estados já nascem preenchidos (zero flash)
  const [invoices, setInvoices] = useState(() => cacheInit?.items || []);
  const [totalInvoices, setTotalInvoices] = useState(() => cacheInit?.total ?? 0);
  const [nextCursor, setNextCursor] = useState(() => cacheInit?.nextCursor ?? null);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(() => cacheInit ? !!cacheInit.hasMore : true);
  const [loadingMore, setLoadingMore] = useState(false);

  // ✅ se há cache, não há loading-screen
  const [loading, setLoading] = useState(() => !cacheInit);

  const clientOptions = useMemo(
    () => [...new Set(invoices.map((i) => i.client).filter(Boolean))],
    [invoices]
  );

  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [poSearchTerm, setPoSearchTerm] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [filterValues, setFilterValues] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    client: "",
    pickup: "",
    delivery: "",
    minPrice: "",
    maxPrice: "",
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  const handleDocToggle = (v) => {
    if (v === selectedDocView) return;

    setSelectedDocView(v);

    const qs = v === "purchase_orders" ? "?view=purchase_orders" : "";
    navigate(`/invoices${qs}`, { replace: true }); // replace evita "spam" no history
  };

  /* === Pretty PDF (react-to-print) === */
  const [printInvoice, setPrintInvoice] = useState(null); // invoice a imprimir/gerar
  const [printMode, setPrintMode] = useState(null); // 'dialog' | 'download' | null
  const [printClient, setPrintClient] = useState(null); // dados completos do client para o PDF
  const [printIssuer, setPrintIssuer] = useState(null); // ✅ dados completos da empresa vindos do backend
  const printRef = useRef(null);

  const purchaseOrdersRef = useRef(null);
  const [poStats, setPoStats] = useState({ total: 0, visible: 0 });

  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyLoaded, setCompanyLoaded] = useState(false); // saber quando terminou

  // NEW: clients (para preencher dados completos do cliente no PDF)
  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  const idle = (cb) => {
    if (typeof window.requestIdleCallback === "function") {
      return window.requestIdleCallback(cb, { timeout: 1500 });
    }
    return window.setTimeout(cb, 300);
  };

  useEffect(() => {
    let alive = true;

    const h = idle(async () => {
      try {
        // ✅ Prefetch PO page 1
        const { data } = await axios.get("/purchaseorders", {
          params: { limit: 20, offset: 0 },
        });

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.purchaseOrders)
          ? data.purchaseOrders
          : [];

        const total =
          typeof data?.total === "number"
            ? data.total
            : typeof data?.count === "number"
            ? data.count
            : typeof data?.meta?.total === "number"
            ? data.meta.total
            : null;

        const nextOffset = list.length;
        const hasMore =
          typeof data?.hasMore === "boolean"
            ? data.hasMore
            : total != null
            ? nextOffset < total
            : list.length === 20;

        if (!alive) return;

        // ✅ grava no mesmo cacheKey do PurchaseOrders.js (instant!)
        try {
          sessionStorage.setItem(
            PO_CACHE_KEY,
            JSON.stringify({
              ts: Date.now(),
              items: list,
              meta: {
                total: typeof total === "number" ? total : 0,
                nextOffset,
                hasMore: !!hasMore,
              },
            })
          );
        } catch {}
      } catch (e) {
        // não bloqueia nada — é só prefetch
        console.warn("⚠️ PO prefetch falhou:", e);
      }
    });

    return () => {
      alive = false;
      if (typeof h === "number") clearTimeout(h);
    };
  }, []);

  const invoicesSentinelRef = useRef(null);

  const fetchInvoicesPage = useCallback(
    async ({ cursor = null, mode = "replace" } = {}) => {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;

      const { data } = await axios.get("/invoices/summary", { params });

      const rows = Array.isArray(data?.invoices) ? data.invoices : [];
      const total = typeof data?.total === "number" ? data.total : 0;
      const newNextCursor = data?.nextCursor ?? null;
      const newHasMore = !!data?.hasMore;

      setTotalInvoices(total);
      setNextCursor(newNextCursor);
      setHasMoreInvoices(newHasMore);

      setInvoices((prev) => {
        // merge por id/invoiceNumber (preserva status local quando o backend vem vazio)
        const prevByKey = new Map(prev.map((x) => [String(x.id ?? x.invoiceNumber), x]));

        const mergedPage = rows.map((r) => {
          const k = String(r.id ?? r.invoiceNumber);
          const old = prevByKey.get(k);
          return old ? { ...r, status: r.status ?? old.status } : r;
        });

        const next = mode === "append" ? [...prev, ...mergedPage] : mergedPage;

        try {
          localStorage.setItem(
            INVOICES_CACHE_KEY,
            JSON.stringify({
              ts: Date.now(),
              items: next,
              total,
              nextCursor: newNextCursor,
              hasMore: newHasMore,
            })
          );
        } catch {}

        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (loading) return;
    if (selectedDocView !== "invoices") return;

    const el = invoicesSentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      async (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;

        if (!hasMoreInvoices) return;
        if (loadingMore) return;
        if (!nextCursor) return;

        try {
          setLoadingMore(true);
          await fetchInvoicesPage({ cursor: nextCursor, mode: "append" });
        } catch (e) {
          console.error("❌ Failed to load more invoices:", e);
        } finally {
          setLoadingMore(false);
        }
      },
      { root: null, rootMargin: "600px 0px", threshold: 0 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [loading, selectedDocView, hasMoreInvoices, loadingMore, nextCursor, fetchInvoicesPage]);

  // Versão normalizada dos clients: garante sempre name/address/postalCode/city/country/phone/email
  const clientsNorm = useMemo(
    () =>
      (clients || []).map((row) => ({
        ...row,
        name: firstNonEmpty(row.name, row.companyName, row.clientName),
        address: firstNonEmpty(
          row.address,
          row.address1,
          row.street,
          row.streetAddress,
          row.street_address
        ),
        postalCode: firstNonEmpty(
          row.postalCode,
          row.postal_code,
          row.zipCode,
          row.zip,
          row.postcode
        ),
        city: firstNonEmpty(row.city, row.town, row.locality),
        country: firstNonEmpty(
          row.country,
          row.country_name,
          row.countryName
        ),
        email: firstNonEmpty(
          row.email,
          row.contactEmail,
          row.companyEmail,
          row.company_email
        ),
        phone: firstNonEmpty(
          row.phone,
          row.phoneNumber,
          row.phone_number,
          row.contactPhone,
          row.companyPhoneNumber,
          row.company_phone_number
        ),
        VAT_TaxInformation: firstNonEmpty(
          row.VAT_TaxInformation,
          row.vatTaxInformation,
          row.vat_tax_id,
          row.vat,
          row.nif,
          row.taxId
        ),
      })),
    [clients]
  );

  useEffect(() => {
    const v = viewFromUrl(location.search);
    setSelectedDocView(v);
  }, [location.search]);

  useEffect(() => {
    if (selectedDocView !== "invoices") return;

    let alive = true;
    const h = idle(async () => {
      try {
        const [carRes, indRes] = await Promise.all([
          axios.get("/carriers"),
          axios.get("/industries"),
        ]);

        if (!alive) return;

        const carriers = Array.isArray(carRes.data)
          ? carRes.data
          : carRes.data?.rows || [];

        const industries = Array.isArray(indRes.data)
          ? indRes.data
          : indRes.data?.rows || [];

        const merged = [
          ...carriers.map((c) => ({ ...c, clientType: "carrier" })),
          ...industries.map((i) => ({ ...i, clientType: "industry" })),
        ];

        setClients(merged || []);
        writeInvoicesAuxCache({ companyProfile, clients: merged || [] });
      } catch (e) {
        console.warn("⚠️ Falhou fetch de carriers/industries:", e);
        if (alive) setClients([]);
      } finally {
        if (alive) setClientsLoaded(true);
      }
    });

    return () => {
      alive = false;
      if (typeof h === "number") clearTimeout(h);
    };
  }, [selectedDocView]);

  const doPrettyPdf = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${t("files.document_prefix", { defaultValue: "Documento-Cobranca" })}-${printInvoice?.invoiceNumber ?? printInvoice?.id}`,
    onAfterPrint: () => {
      setPrintInvoice(null);
      setPrintClient(null);
      setPrintIssuer(null);
      setPrintMode(null);
    },
  });

  // Dispara o print dialog apenas quando printMode === 'dialog'
  useEffect(() => {
    if (!printInvoice || printMode !== "dialog") return;

    let attempts = 0;
    let cancelled = false;

    const tryPrint = () => {
      if (cancelled) return;
      if (printRef.current) {
        doPrettyPdf?.();
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        setTimeout(tryPrint, 50);
      } else {
        console.warn("InvoicePrint: ref ainda não disponível para imprimir o documento.");
        setPrintInvoice(null);
        setPrintMode(null);
      }
    };

    const t = setTimeout(tryPrint, 0);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [printInvoice, printMode, doPrettyPdf]);

  useEffect(() => {
    (async () => {
      try {
        // 1) Dados base da empresa (morada, logo, etc.)
        const { data } = await axios.get("/company-details");
        const row = Array.isArray(data) ? data[0] : data;

        const base = {
          name: firstNonEmpty(row?.company_name, row?.name, row?.companyName),
          address: firstNonEmpty(
            row?.address,
            row?.address1,
            row?.street,
            row?.streetAddress,
            row?.street_address
          ),
          city: firstNonEmpty(row?.city, row?.town, row?.locality),
          postalCode: firstNonEmpty(
            row?.postal_code,
            row?.zipCode,
            row?.postalCode,
            row?.zip,
            row?.postcode
          ),
          country: firstNonEmpty(
            row?.country,
            row?.country_name,
            row?.countryName
          ),

          email: firstNonEmpty(
            row?.companyEmail,
            row?.company_email,
            row?.email,
            row?.contactEmail
          ),
          phone: firstNonEmpty(
            row?.companyPhoneNumber,
            row?.company_phone_number,
            row?.phoneNumber,
            row?.phone_number,
            row?.phone,
            row?.company_phone,
            row?.contactPhone
          ),
          website: firstNonEmpty(
            row?.website,
            row?.company_website,
            row?.site,
            row?.url
          ),
          vat: firstNonEmpty(
            row?.vat_tax_id,
            row?.vatTaxId,
            row?.vatTaxID,
            row?.VAT_TaxInformation,
            row?.vatTaxInformation,
            row?.company_registration_number,
            row?.companyRegistrationNumber,
            row?.vat,
            row?.vatNumber,
            row?.nif,
            row?.taxId
          ),
          
          // ✅ mantém também o nome original, caso o InvoicePrint leia diretamente
          vat_tax_id: firstNonEmpty(
            row?.vat_tax_id,
            row?.vatTaxId,
            row?.vatTaxID,
            row?.VAT_TaxInformation,
            row?.vatTaxInformation,
            row?.company_registration_number,
            row?.companyRegistrationNumber,
            row?.vat,
            row?.vatNumber,
            row?.nif,
            row?.taxId
          ),

          logoUrl: absoluteFromApiBase(
            firstNonEmpty(
              row?.photoCompany,
              row?.photo_company,
              row?.logo,
              row?.logoUrl
            )
          ),

          paymentMethod: firstNonEmpty(row?.paymentMethod, "transferência"),
          iban: firstNonEmpty(row?.iban),
          bic: firstNonEmpty(row?.bic),
          paymentInfo: firstNonEmpty(row?.paymentInfo),
        };

        // 2) Sobrepor IBAN/BIC com o que vem das Settings → Financial Information
        try {
          const { data: fi } = await axios.get(
            "/company/financial-information"
          );
          if (fi) {
            base.iban = firstNonEmpty(fi.iban, base.iban);
            base.bic = firstNonEmpty(fi.bic, fi.swift, base.bic);
            // se no futuro tiveres paymentMethod / currency também aqui:
            // base.paymentMethod = firstNonEmpty(fi.paymentMethod, base.paymentMethod);
          }
        } catch (fiErr) {
          console.warn(
            "⚠️ Falhou a leitura de /company/financial-information:",
            fiErr
          );
        }

        setCompanyProfile(base);
        writeInvoicesAuxCache({ companyProfile: base, clients }); // mantém o que já existe
      } catch (err) {
        console.warn("⚠️ Falhou a leitura do company profile:", err);
        setCompanyProfile(null);
      } finally {
        setCompanyLoaded(true);
      }
    })();
  }, []);

  // Export PDF por linha (download automático via html2canvas)
  async function exportRowPdf(invoice) {
    if (!invoice?.id) {
      alert(t("alerts.missing_id"));
      return;
    }
    
    let issuerForPrint = null;
    let clientForPrint = {
      name: invoice.client || invoice.clientName || "",
      VAT_TaxInformation: "",
      address: "",
      postalCode: "",
      city: "",
      country: "",
      phone: "",
      email: "",
    };
    
    let invoiceForPrintFromBackend = null;
    
    try {
      const { data } = await axios.get(`/invoices/${invoice.id}/print-context`);
    
      console.log("[exportRowPdf] print-context =", data);
      console.log("[exportRowPdf] issuer vat =", {
        vat: data?.issuer?.vat,
        vat_tax_id: data?.issuer?.vat_tax_id,
        nif: data?.issuer?.nif,
      });
    
      issuerForPrint = data?.issuer || null;

      clientForPrint = {
        ...clientForPrint,
        ...(data?.client || {}),
      
        // ✅ normaliza o NIF do cliente para vários nomes possíveis
        VAT_TaxInformation:
          data?.client?.VAT_TaxInformation ||
          data?.client?.vatTaxInformation ||
          data?.client?.vat_tax_id ||
          data?.client?.vat ||
          data?.client?.nif ||
          data?.client?.taxId ||
          clientForPrint.VAT_TaxInformation ||
          "",
      };
      
      invoiceForPrintFromBackend = data?.invoice || null;
    } catch (err) {
      console.warn("⚠️ Falhou /invoices/:id/print-context. A usar fallback frontend.", err);
    
      if (!companyLoaded) {
        alert(t("alerts.company_loading"));
        return;
      }
    
      if (!clientsLoaded) {
        console.warn(
          "Clientes ainda a carregar — o documento pode sair só com o nome do cliente."
        );
      }
    }

    try {
      // ✅ Só usa fallback frontend se o backend NÃO trouxe dados completos do cliente
      const backendHasClientVat =
        clientForPrint?.VAT_TaxInformation ||
        clientForPrint?.vatTaxInformation ||
        clientForPrint?.vat_tax_id ||
        clientForPrint?.vat ||
        clientForPrint?.nif ||
        clientForPrint?.taxId;
    
      if (
        !backendHasClientVat &&
        clientsLoaded &&
        Array.isArray(clientsNorm) &&
        clientsNorm.length
      ) {
        const invoiceLike = {
          clientName: invoice.client || invoice.clientName || "",
          client: invoice.client || invoice.clientName || "",
          clientType: invoice.clientType || "",
          industryClientId: invoice.industryClientId ?? null,
          carrierClientId: invoice.carrierClientId ?? null,
          clientId:
            invoice.clientId ??
            invoice.industryClientId ??
            invoice.carrierClientId ??
            null,
        };
    
        const info = selectClientInfo(invoiceLike, clientsNorm);
    
        if (info) {
          clientForPrint = {
            ...clientForPrint,
    
            name: firstNonEmpty(info.name, clientForPrint.name),
    
            VAT_TaxInformation: firstNonEmpty(
              clientForPrint.VAT_TaxInformation,
              clientForPrint.vatTaxInformation,
              clientForPrint.vat_tax_id,
              clientForPrint.vat,
              clientForPrint.nif,
              clientForPrint.taxId,
              info.VAT_TaxInformation,
              info.vatTaxInformation,
              info.vat_tax_id,
              info.vat,
              info.nif,
              info.taxId
            ),
    
            address: firstNonEmpty(clientForPrint.address, info.address),
            postalCode: firstNonEmpty(clientForPrint.postalCode, info.postalCode),
            city: firstNonEmpty(clientForPrint.city, info.city),
            country: firstNonEmpty(clientForPrint.country, info.country),
            phone: firstNonEmpty(clientForPrint.phone, info.phone, info.phoneNumber),
            email: firstNonEmpty(clientForPrint.email, info.email, info.contactEmail),
          };
        }
      }
    } catch (e) {
      console.warn("⚠️ Falha ao resolver dados do cliente para o documento:", e);
    }

    // guardar dados calculados para o componente InvoicePrint
    setPrintIssuer(issuerForPrint);
    setPrintClient(clientForPrint);

    setPrintMode("download");
    setPrintInvoice({
      ...invoice,
      ...(invoiceForPrintFromBackend || {}),
    });

    let attempts = 0;
    const waitForRef = () =>
      new Promise((resolve, reject) => {
        (function tick() {
          if (printRef.current) return resolve();
          attempts += 1;
          if (attempts > 80)
            return reject(new Error("InvoicePrint não ficou pronto a tempo."));
          setTimeout(tick, 25);
        })();
      });

    try {
      await waitForRef();

      const jsPDFmod = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");
      const jsPDF = jsPDFmod.jsPDF || jsPDFmod.default || jsPDFmod;

      const el = printRef.current;

      const canvas = await html2canvas(el, {
        // ✅ antes era 2 — isso criava uma imagem gigante dentro do PDF
        scale: 1.35,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: 794,
        imageTimeout: 8000,
      });

      // ✅ JPEG comprimido em vez de PNG.
      // Para invoices/documentos, isto reduz brutalmente o tamanho.
      const imgData = canvas.toDataURL("image/jpeg", 0.82);

      // A4 em pontos
      const doc = new jsPDF({
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;
      const x = (pageW - imgW) / 2;
      const y = (pageH - imgH) / 2;

      doc.addImage(imgData, "JPEG", x, y, imgW, imgH, undefined, "FAST");
      doc.save(`${t("files.document_prefix", { defaultValue: "Documento-Cobranca" })}-${invoice.invoiceNumber ?? invoice.id}.pdf`);
    } catch (err) {
      console.error("❌ Falha a gerar PDF:", err);
      alert(t("alerts.pdf_failed"));
    } finally {
      setPrintInvoice(null);
      setPrintClient(null);
      setPrintIssuer(null);
      setPrintMode(null);
    }
  }

  async function handleDelete(invoice) {
    const visibleId = invoice.id ?? invoice.invoiceNumber;
    if (!visibleId) {
      alert(t("alerts.missing_id"));
      return;
    }
  
    if (!window.confirm(t("alerts.delete_confirm", { id: visibleId }))) return;
  
    const previousInvoices = invoices;
  
    try {
      setDeletingId(visibleId);
  
      setInvoices((prev) =>
        prev.filter((x) => String(x.id ?? x.invoiceNumber) !== String(visibleId))
      );
  
      if (invoice.id != null) {
        await axios.delete(`/invoices/${invoice.id}`);
      } else {
        throw new Error("Missing invoice.id for delete request");
      }
    } catch (err) {
      console.error("❌ Delete failed:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        url: err?.config?.url,
        method: err?.config?.method,
      });
  
      setInvoices(previousInvoices); // rollback visual
      alert(t("alerts.delete_failed"));
    } finally {
      setDeletingId(null);
    }
  }

    async function handleSendInvoiceEmail(invoice) {
    const visibleId = invoice.id ?? invoice.invoiceNumber;
    if (!visibleId) {
      alert(t("alerts.missing_id"));
      return;
    }

    if (invoice.id == null) {
      alert(t("alerts.missing_id"));
      return;
    }

    try {
      setSendingEmailId(visibleId);

      const { data } = await axios.post(`/invoices/${invoice.id}/send-email`);

      if (data?.status) {
        const nextStatus = String(data.status).toUpperCase();

        setInvoices((prev) => {
          const merged = prev.map((x) => {
            const k = String(x.id ?? x.invoiceNumber);
            return k === String(visibleId) ? { ...x, status: nextStatus } : x;
          });

          try {
            localStorage.setItem(
              INVOICES_CACHE_KEY,
              JSON.stringify({
                ts: Date.now(),
                items: merged,
                total: typeof totalInvoices === "number" ? totalInvoices : 0,
                nextCursor: nextCursor ?? null,
                hasMore: !!hasMoreInvoices,
              })
            );
          } catch {}

          return merged;
        });
      }

      alert(
        t("actions.send_email_success", {
          defaultValue: `Billing document sent successfully to ${data?.email || "billing email"}.`,
          email: data?.email || "billing email",
        })
      );
    } catch (err) {
      console.error("❌ Send invoice email failed:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        url: err?.config?.url,
        method: err?.config?.method,
      });

      alert(
        err?.response?.data?.error ||
          t("actions.send_email_failed", {
            defaultValue: "Failed to send billing document email.",
          })
      );
    } finally {
      setSendingEmailId(null);
    }
  }

  function parseYMD(dateLike) {
    if (!dateLike) return null;
    if (typeof dateLike === "string") {
      const m = dateLike.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return { y: m[1], m: m[2], d: m[3] };
    }
    const d = new Date(dateLike);
    if (Number.isNaN(d)) return null;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear());
    return { y: yy, m: mm, d: dd };
  }

  function formatDMY(dateLike) {
    const ymd = parseYMD(dateLike);
    return ymd ? `${ymd.d}-${ymd.m}-${ymd.y}` : "—";
  }

  function formatHHMM(hourLike) {
    if (!hourLike) return "";
    const [hhRaw = "", mmRaw = ""] = String(hourLike).split(":");
    const hh = String(hhRaw).padStart(2, "0");
    const mm = String(mmRaw || "00").padStart(2, "0");
    return `${hh}:${mm}`;
  }

  function formatDateTime(dateLike, hourLike) {
    const dateOut = formatDMY(dateLike);
    const timeOut = formatHHMM(hourLike);
    if (dateOut === "—" && !timeOut) return "—";
    if (dateOut !== "—" && timeOut) return `${timeOut}  ${dateOut}`;
    return timeOut || dateOut;
  }

  // resolve clientType a partir do nome do cliente usando a lista merged (carriers+industries)
  function inferClientTypeByName(clientName, clientsNorm) {
    const name = String(clientName || "").trim().toLowerCase();
    if (!name) return "";

    const hits = (clientsNorm || []).filter(
      (c) => String(c?.name || "").trim().toLowerCase() === name
    );

    if (!hits.length) return "";

    // ✅ se houver mais do que um match com tipos diferentes, é ambíguo -> não adivinhar
    const types = [...new Set(hits.map((h) => h?.clientType).filter(Boolean))];
    if (types.length !== 1) return "";

    return types[0] || "";
  }

  function toYYYYMMDD(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  }

  function daysDiff(fromYmd, toYmd) {
    if (!fromYmd || !toYmd) return null;
    const a = new Date(fromYmd);
    const b = new Date(toYmd);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    const ms = b - a;
    const d = Math.round(ms / (1000 * 60 * 60 * 24));
    return Number.isFinite(d) ? d : null;
  }

  const didFetchInvoicesRef = useRef(false);

  useEffect(() => {
    // ✅ evita duplicar requests no dev (StrictMode)
    if (didFetchInvoicesRef.current) return;
    didFetchInvoicesRef.current = true;

    let alive = true;

    (async () => {
      try {
        await fetchInvoicesPage({ cursor: null, mode: "replace" });
      } catch (err) {
        console.error("❌ Failed to fetch invoices (paged):", err);
        if (alive) setInvoices((prev) => (prev?.length ? prev : []));
      } finally {
        // ✅ se não havia cache, agora já podes tirar loading
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [fetchInvoicesPage]);

  // Live updates via websocket
  useEffect(() => {
    const s = getSocket();

    const shapeForSummary = (r) => {
      const pickupLocation = r.pickupLocation ?? r.pickup ?? null;
      const deliveryLocation = r.deliveryLocation ?? r.delivery ?? null;

      const pickupExists = !!pickupLocation;
      const deliveryExists = !!deliveryLocation;

      const pickupCount =
        typeof r.pickupCount === "number"
          ? r.pickupCount
          : pickupExists
          ? 1
          : 0;
      const deliveryCount =
        typeof r.deliveryCount === "number"
          ? r.deliveryCount
          : deliveryExists
          ? 1
          : 0;

      const pickupList = Array.isArray(r.pickupList)
        ? r.pickupList
        : pickupExists
        ? [pickupLocation]
        : [];
      const deliveryList = Array.isArray(r.deliveryList)
        ? r.deliveryList
        : deliveryExists
        ? [deliveryLocation]
        : [];

      return {
        id: r.id,
        invoiceNumber: r.invoiceNumber ?? r.invoice_id ?? null,
        issueDate: r.issueDate ?? r.date ?? null,
        paymentDueDate: r.paymentDueDate ?? null,
        net_payment_days_carrier: r.net_payment_days_carrier ?? null,
      
        loadId: r.loadId ?? r.load_id ?? null,
        loadNumber: r.loadNumber ?? r.load?.loadNumber ?? null,
      
        clientType: r.clientType ?? r.client_type ?? null,
        industryClientId: r.industryClientId ?? r.industry_client_id ?? null,
        carrierClientId: r.carrierClientId ?? r.carrier_client_id ?? null,
        client: r.client ?? null,
        status: r.status ?? null,
        
        vatRate: r.vatRate ?? r.vat_rate ?? null,

        pickupCount,
        deliveryCount,
        pickupLocation,
        pickupDate: r.pickupDate ?? null,
        pickupHour: r.pickupHour ?? null,
        deliveryLocation,
        deliveryDate: r.deliveryDate ?? null,
        deliveryHour: r.deliveryHour ?? null,

        pickupList,
        deliveryList,

        price: r.price ?? null,
        date: r.issueDate ?? r.date ?? null,
      };
    };

    const subscribe = () => s.emit("subscribe:invoices");
    subscribe();
    s.on("connect", subscribe);
    s.on("reconnect", subscribe);

    const onCreated = (inv) =>
      setInvoices((prev) => {
        const shaped = shapeForSummary(inv);
        const key = String(shaped.id ?? shaped.invoiceNumber);
        const exists = prev.some((x) => String(x.id ?? x.invoiceNumber) === key);
        const next = exists ? prev : [shaped, ...prev];

        // ✅ mantém cache sincronizado
        persistInvoicesCache({
          items: next,
          total: (totalInvoices || 0) + (exists ? 0 : 1),
          nextCursor,
          hasMore: hasMoreInvoices,
        });

        return next;
      });

    const onUpdated = (inv) =>
      setInvoices((prev) => {
        const shaped = shapeForSummary(inv);
        const key = String(shaped.id ?? shaped.invoiceNumber);
        return prev.map((x) => {
          if (String(x.id ?? x.invoiceNumber) !== key) return x;
          return { ...x, ...shaped, status: shaped.status ?? x.status };
        });
      });

    const onDeleted = ({ id }) =>
      setInvoices((prev) =>
        prev.filter((x) => String(x.id ?? x.invoiceNumber) !== String(id))
      );

    s.on("invoice:created", onCreated);
    s.on("invoice:updated", onUpdated);
    s.on("invoice:deleted", onDeleted);

    return () => {
      s.emit("unsubscribe:invoices");
      s.off("connect", subscribe);
      s.off("reconnect", subscribe);
      s.off("invoice:created", onCreated);
      s.off("invoice:updated", onUpdated);
      s.off("invoice:deleted", onDeleted);
    };
  }, []);

  function addDaysYMD(yyyyMmDd, days) {
    if (!yyyyMmDd || typeof days !== "number") return null;
    const d = new Date(yyyyMmDd);
    if (Number.isNaN(d)) return null;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }

  const normalized = invoices.map((inv) => {
    const baseDate = inv.issueDate ?? inv.date ?? null;
    const netRaw = inv.net_payment_days_carrier;
    const net = Number.isInteger(netRaw)
      ? netRaw
      : inv.net_payment_days_carrier != null
      ? parseInt(netRaw, 10)
      : null;

    const effectiveDue =
      inv.paymentDueDate ??
      (baseDate && Number.isFinite(net) ? addDaysYMD(baseDate, net) : null);

      const loadDisplay = inv.loadNumber ?? inv.loadId ?? "—";

    return {
      id: inv.id ?? null,
    
      loadId: inv.loadId ?? null,
      loadNumber: inv.loadNumber ?? null,
      loadDisplay,
    
      invoiceNumber: inv.invoiceNumber ?? null,
      date: inv.issueDate ?? inv.date ?? "—",
      client: inv.client ?? "—",
      pickup: inv.pickupLocation ?? "—",
      delivery: inv.deliveryLocation ?? "—",
      price: inv.price ?? "—",
      status: inv.status || "DRAFT",
      
      vatRate: inv.vatRate ?? inv.vat_rate ?? null,
      
      expiryOption: null,
      clientType: inv.clientType ?? inv.client_type ?? "",
      industryClientId: inv.industryClientId ?? inv.industry_client_id ?? null,
      carrierClientId: inv.carrierClientId ?? inv.carrier_client_id ?? null,
      pickupDate: inv.pickupDate ?? null,
      pickupHour: inv.pickupHour ?? null,
      deliveryDate: inv.deliveryDate ?? null,
      deliveryHour: inv.deliveryHour ?? null,
      paymentDueDate: effectiveDue,
    };
  });

  const filteredInvoices = normalized.filter((invoice) => {
    const term = String(invoiceSearchTerm || "").trim().toLowerCase();

    const matchesSearch =
      !term ||
      String(invoice.invoiceNumber ?? "").toLowerCase().includes(term) ||
      String(invoice.id ?? "").toLowerCase().includes(term) ||
      String(invoice.loadDisplay ?? "").toLowerCase().includes(term) ||
      String(invoice.client ?? "").toLowerCase().includes(term) ||
      String(invoice.pickup ?? "").toLowerCase().includes(term) ||
      String(invoice.delivery ?? "").toLowerCase().includes(term) ||
      String(invoice.price ?? "").toLowerCase().includes(term) ||
      String(invoice.status ?? "").toLowerCase().includes(term);

    const matchesStatus =
      !filterValues.status ||
      filterValues.status === "all" ||
      String(invoice.status || "DRAFT").toLowerCase() ===
        String(filterValues.status).toLowerCase();

    const matchesClient =
      !filterValues.client ||
      String(invoice.client || "").toLowerCase() ===
        String(filterValues.client).toLowerCase();

    const matchesPickup =
      !filterValues.pickup ||
      String(invoice.pickup || "")
        .toLowerCase()
        .includes(String(filterValues.pickup).toLowerCase());

    const matchesDelivery =
      !filterValues.delivery ||
      String(invoice.delivery || "")
        .toLowerCase()
        .includes(String(filterValues.delivery).toLowerCase());

    const matchesDate = dateWithin(
      invoice.date,
      filterValues.dateFrom,
      filterValues.dateTo
    );

    const invoicePrice = parseMoneyValue(invoice.price);
    const minPrice = parseMoneyValue(filterValues.minPrice);
    const maxPrice = parseMoneyValue(filterValues.maxPrice);

    const matchesMinPrice =
      minPrice == null ||
      invoicePrice == null ||
      invoicePrice >= minPrice;

    const matchesMaxPrice =
      maxPrice == null ||
      invoicePrice == null ||
      invoicePrice <= maxPrice;

    return (
      matchesSearch &&
      matchesStatus &&
      matchesClient &&
      matchesPickup &&
      matchesDelivery &&
      matchesDate &&
      matchesMinPrice &&
      matchesMaxPrice
    );
  });

  /* ---------- Export global (toolbar) ---------- */
  const doExport = async ({ from, to, format }) => {
    const scope = filteredInvoices.filter((inv) =>
      dateWithin(inv.date, from, to)
    );

    if (!scope.length) {
      alert(t("export.no_rows"));
      return;
    }

    const flat = scope.map((i) => ({
      invoiceId: i.invoiceNumber ?? i.id,
      loadId: i.loadId,
      date: i.date,
      paymentDueDate: i.paymentDueDate,
      client: i.client,
      pickup: i.pickup,
      delivery: i.delivery,
      price: i.price,
      status: i.status || "DRAFT",
      pickupDateTime: formatDateTime(i.pickupDate, i.pickupHour),
      deliveryDateTime: formatDateTime(i.deliveryDate, i.deliveryHour),
    }));

    const stamp = `${from || "all"}_to_${to || "all"}`;

    if (format === "csv") {
      const csv = toCSV(flat);
      download(`${t("files.export_prefix")}_${stamp}.csv`, "text/csv;charset=utf-8;", csv);
      setShowExportModal(false);
      return;
    }

    if (format === "pdf") {
      try {
        const jsPDFmod = await import("jspdf");
        const autoTableMod = await import("jspdf-autotable");
        const jsPDF = jsPDFmod.default || jsPDFmod.jsPDF || jsPDFmod;
        const autoTable =
          autoTableMod.default || autoTableMod.autoTable || autoTableMod;

        const doc = new jsPDF();
        const head = [Object.keys(flat[0])];
        const body = flat.map((r) => Object.values(r));
        autoTable(doc, {
          head,
          body,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [1, 157, 238] },
          margin: 10,
        });
        doc.save(`${t("files.export_prefix")}_${stamp}.pdf`);
        setShowExportModal(false);
      } catch (e) {
        console.error(e);
        alert(t("export.install_pdf"));
      }
      return;
    }

    // XLSX
    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(flat);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Billing Documents");
      XLSX.writeFile(wb, `${t("files.export_prefix")}_${stamp}.xlsx`);
      setShowExportModal(false);
    } catch (e) {
      console.error(e);
      alert(t("export.install_xlsx"));
    }
  };

  /* ---------- Navegação linha ---------- */
  function handleRowClick(invoice, e) {
    // não abrir se clicou em ícones/status
    if (
      e.target.classList.contains("invoices-action-icon") ||
      e.target.closest?.(".invoice-status-wrapper")
    )
      return;

    const rowPkId = invoice.id; // ✅ PK obrigatório para editar
    if (rowPkId == null) {
      alert(t("alerts.missing_id"));
      return;
    }

    // ✅ 1) Usa o que já vem da summary (instantâneo)
    // ✅ 2) Só tenta inferir se vier vazio
    const clientTypeFromRow = String(invoice.clientType || "").trim().toLowerCase();
    const inferredClientType = clientTypeFromRow
      ? ""
      : inferClientTypeByName(invoice.client, clientsNorm);

    const finalClientType = clientTypeFromRow || inferredClientType || "";

    // calcula expiryDays a partir de issueDate + paymentDueDate (que tu já tens!)
    const issueYmd = toYYYYMMDD(invoice.date);
    const dueYmd = toYYYYMMDD(invoice.paymentDueDate);
    const expiryDays = daysDiff(issueYmd, dueYmd);

    navigate(`/invoices/edit/${rowPkId}`, {
      state: {
        prefill: {
          id: rowPkId,
          invoiceNumber: invoice.invoiceNumber ?? null,

          clientName: invoice.client,
          clientType: finalClientType,

          pickupPlace: invoice.pickup,
          deliveryPlace: invoice.delivery,
          price: invoice.price,
          issueDate: issueYmd,

          // ✅ o EditInvoice já normaliza isto para expiryOption via expiryOptions
          expiryDays: Number.isFinite(expiryDays) ? expiryDays : null,

          status: invoice.status,
          paymentDueDate: dueYmd, // opcional (debug/consistência)
        },
      },
    });
  }

  const totalCount = isPO ? poStats.total : (totalInvoices || 0);
  const visibleCount = isPO ? poStats.visible : filteredInvoices.length;

  const companyDefaults = {
    name: t("defaults.company_name"),
    address: "",
    postalCode: "",
    city: "",
    country: "",
    phone: "",
    email: "",
    website: "",
  
    // ✅ ambos para compatibilidade com InvoicePrint
    vat: "",
    vat_tax_id: "",
  
    logoUrl: "",
    paymentMethod: t("defaults.payment_method"),
    iban: "",
    bic: "",
    paymentInfo: "",
  };

  const companyForPrint = { ...companyDefaults, ...(companyProfile || {}) };

  console.log("[Invoices] companyProfile =", companyProfile);
  console.log("[Invoices] companyForPrint =", companyForPrint);
  console.log("[Invoices] companyForPrint vat =", {
    vat: companyForPrint?.vat,
    vat_tax_id: companyForPrint?.vat_tax_id,
    vatTaxId: companyForPrint?.vatTaxId,
    nif: companyForPrint?.nif,
  });

  const hasActiveInvoiceFilters =
    filterValues.status !== "all" ||
    !!filterValues.dateFrom ||
    !!filterValues.dateTo ||
    !!filterValues.client ||
    !!filterValues.pickup ||
    !!filterValues.delivery ||
    !!filterValues.minPrice ||
    !!filterValues.maxPrice;

  const hasInvoiceSearch = String(invoiceSearchTerm || "").trim().length > 0;
  const hasInvoiceSearchOrFilters = hasInvoiceSearch || hasActiveInvoiceFilters;
  const hasAnyInvoices = normalized.length > 0;

  return (
    <div className="invoices-page">
      {/* Header slim */}
      <section className="invoices-card invoices-toolbar">
        {/* LEFT */}
        <div className="invoices-toolbar__left">
          <div className="invoices-stats">
            <span className="invoices-pill">
              {t("toolbar.total")}: <strong>{totalCount}</strong>
            </span>
            <span className="invoices-dot">•</span>
            <span className="invoices-pill">
              {t("toolbar.showing")}: <strong>{visibleCount}</strong>
            </span>
          </div>
        </div>

        {/* CENTER: Toggle */}
        <div className="invoices-toolbar__center">
          <div
            className="invoices-toggle-container"
            role="tablist"
            aria-label={t("toggle.aria")}
          >
            <div
              className="invoices-toggle-slider"
              data-view={selectedDocView}
              aria-hidden="true"
            />

            <button
              className={`invoices-toggle-button ${selectedDocView === "invoices" ? "active" : ""}`}
              onClick={() => handleDocToggle("invoices")}
              role="tab"
              aria-selected={selectedDocView === "invoices"}
              aria-controls="invoices-panel-invoices"
              id="invoices-tab-invoices"
              type="button"
            >
              {t("toggle.invoices")}
            </button>

            <button
              className={`invoices-toggle-button ${selectedDocView === "purchase_orders" ? "active" : ""}`}
              onClick={() => handleDocToggle("purchase_orders")}
              role="tab"
              aria-selected={selectedDocView === "purchase_orders"}
              aria-controls="invoices-panel-purchase-orders"
              id="invoices-tab-purchase-orders"
              type="button"
            >
              {t("toggle.purchase_orders")}
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="invoices-toolbar__right">
          {/* Search */}
          <div className="invoices-search">
            <FaSearch className="invoices-search__icon" />
            <input
              type="text"
              placeholder={
                isPO
                  ? tPO("toolbar.search_placeholder", "Search purchase orders...")
                  : t("search.placeholder")
              }
              className="invoices-search__input"
              value={isPO ? poSearchTerm : invoiceSearchTerm}
              onChange={(e) => {
                const v = e.target.value;

                if (isPO) {
                  setPoSearchTerm(v);
                  // ✅ empurra para o componente de PO (se existir)
                  purchaseOrdersRef.current?.setSearchTerm?.(v);
                } else {
                  setInvoiceSearchTerm(v);
                }
              }}
              aria-label={
                isPO
                  ? tPO("search.aria", "Search purchase orders")
                  : t("search.aria")
              }
            />

            {(isPO ? poSearchTerm : invoiceSearchTerm) && (
              <button
                type="button"
                className="invoices-search__clear"
                onClick={() => {
                  if (isPO) {
                    setPoSearchTerm("");
                    purchaseOrdersRef.current?.setSearchTerm?.("");
                  } else {
                    setInvoiceSearchTerm("");
                  }
                }}
                aria-label={t("search.clear_aria")}
                title={t("search.clear_title")}
              >
                ✕
              </button>
            )}
          </div>

          {/* Buttons */}
          <div className="invoices-actions">
            <button
              className={`invoices-btn ${
                !isPO && hasActiveInvoiceFilters ? "invoices-btn--active" : ""
              }`}
              title={isPO ? tPO("buttons.filter", "Filter") : t("buttons.add_filter")}
              onClick={() => {
                if (isPO) purchaseOrdersRef.current?.openFilter?.();
                else setIsFilterModalOpen(true);
              }}
              type="button"
            >
              <FaFilter />
              <span>
                {isPO ? tPO("buttons.filter", "Filter") : t("buttons.add_filter")}
              </span>

              {!isPO && hasActiveInvoiceFilters && (
                <span className="invoices-btn__badge">•</span>
              )}
            </button>

            <button
              className="invoices-btn"
              title={isPO ? tPO("buttons.export", "Export") : t("buttons.export")}
              onClick={() => {
                if (isPO) purchaseOrdersRef.current?.openExport?.();
                else setShowExportModal(true);
              }}
              type="button"
            >
              <FaDownload /> <span>{isPO ? tPO("buttons.export", "Export") : t("buttons.export")}</span>
            </button>

            <button
              className="invoices-btn invoices-btn--primary"
              onClick={() => {
                if (selectedDocView === "invoices") {
                  navigate("/invoices/create");
                } else {
                  // ✅ OPEN CreatePurchaseOrders
                  navigate("/purchaseorders/create");
                  // (se preferires o padrão com hífen, usa: navigate("/purchase-orders/create");)
                }
              }}
              type="button"
            >
            {selectedDocView === "invoices"
              ? t("buttons.new_invoice")
              : tPO("buttons.new_purchase_order", "+ New Purchase Order")}
            </button>
          </div>
        </div>
      </section>

      {/* Loading só se não houver nada para mostrar */}
      {selectedDocView === "invoices" && loading && invoices.length === 0 && (
        <section className="invoices-card invoices-loading-card">
          <div className="invoices-loading">{t("loading")}</div>
        </section>
      )}

      {/* PO */}
      {selectedDocView === "purchase_orders" && (
        <section
          className="invoices-card"
          id="invoices-panel-purchase-orders"
          role="tabpanel"
          aria-labelledby="invoices-tab-purchase-orders"
        >
          <PurchaseOrders ref={purchaseOrdersRef} onStatsChange={setPoStats} />
        </section>
      )}

      {/* Invoices (render sempre; cache aparece instant) */}
      {selectedDocView === "invoices" && (
        <section
          className="invoices-card"
          id="invoices-panel-invoices"
          role="tabpanel"
          aria-labelledby="invoices-tab-invoices"
        >
          <div className="invoices-table">
            {/* opcional: mini “atualizar…” sem bloquear */}
            {loading && invoices.length > 0 && (
              <div className="invoices-loading" style={{ padding: "8px 0" }}>
                {t("loading")}
              </div>
            )}
            <div className="invoices-table">
              <div className="invoices-table__header">
                <div>{t("table.invoice_id")}</div>
                <div>{t("table.load_id")}</div>
                <div>{t("table.date")}</div>
                <div>{t("table.payment_due")}</div>
                <div>{t("table.pickup_place")}</div>
                <div>{t("table.delivery_place")}</div>
                <div>{t("table.client")}</div>
                <div>{t("table.price")}</div>
                <div>{t("table.status")}</div>
                <div className="invoices-col-actions">{t("table.actions")}</div>
              </div>
            </div>

            {filteredInvoices.length > 0 ? (
              filteredInvoices.map((invoice) => {
                const rowKey = `inv-${invoice.id ?? "noid"}-${invoice.invoiceNumber ?? "nonum"}-${invoice.loadId ?? "noload"}`;
                const visibleId = invoice.invoiceNumber ?? invoice.id;

                return (
                  <div
                    className="invoices-row"
                    key={rowKey}
                    onClick={(e) => handleRowClick(invoice, e)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleRowClick(invoice, e)
                    }
                    style={{ position: "relative" }}
                  >
                    <div title={visibleId}>{visibleId}</div>
                    <div title={invoice.loadDisplay}>{invoice.loadDisplay}</div>
                    <div title={formatDMY(invoice.date)}>
                      {formatDMY(invoice.date)}
                    </div>
                    <div title={formatDMY(invoice.paymentDueDate)}>
                      {formatDMY(invoice.paymentDueDate)}
                    </div>

                    <div>
                      <div>{invoice.pickup}</div>
                      <div className="invoices-subline">
                        {formatDateTime(invoice.pickupDate, invoice.pickupHour)}
                      </div>
                    </div>

                    <div>
                      <div>{invoice.delivery}</div>
                      <div className="invoices-subline">
                        {formatDateTime(
                          invoice.deliveryDate,
                          invoice.deliveryHour
                        )}
                      </div>
                    </div>

                    <div title={invoice.client}>{invoice.client}</div>
                    <div title={invoice.price}>{invoice.price}</div>

                    <div
                      className="invoice-status-wrapper"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select
                        classNamePrefix="invoice-status-react-select"
                        value={getInvoiceStatusOptions(t).find(
                          (opt) => opt.value === String(invoice.status || "DRAFT").toUpperCase()
                        )}
                        options={getInvoiceStatusOptions(t)}
                        isSearchable={false}
                        menuPlacement="bottom"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={buildInvoiceStatusSelectStyles(invoice.status)}
                        onChange={async (selected) => {
                          const next = String(selected?.value || "DRAFT").toUpperCase();

                          const rowKey = String(invoice.id ?? invoice.invoiceNumber);

                          // 1) optimistic UI update
                          setInvoices((prev) =>
                            prev.map((x) => {
                              const k = String(x.id ?? x.invoiceNumber);
                              return k === rowKey ? { ...x, status: next } : x;
                            })
                          );

                          try {
                            // 2) persist on backend + read back the persisted row
                            let resp;
                            if (invoice.id != null) {
                              resp = await axios.put(`/invoices/${invoice.id}`, { status: next });
                            } else if (invoice.invoiceNumber != null) {
                              resp = await axios.put(`/invoices/by-number/${invoice.invoiceNumber}`, { status: next });
                            } else {
                              throw new Error("Missing invoice id/invoiceNumber for status update");
                            }

                            // 3) force state to the persisted value (wins vs any late GET overwrites)
                            const persisted = resp?.data || {};
                            const persistedStatus = String(persisted.status || next).toUpperCase();

                            setInvoices((prev) => {
                              const merged = prev.map((x) => {
                                const k = String(x.id ?? x.invoiceNumber);
                                if (k !== rowKey) return x;
                                return { ...x, ...persisted, status: persistedStatus };
                              });

                              // 4) keep cache in sync too (prevents “flashback” from cached rows)
                              try {
                                localStorage.setItem(
                                  INVOICES_CACHE_KEY,
                                  JSON.stringify({
                                    ts: Date.now(),
                                    items: merged,
                                    total: typeof totalInvoices === "number" ? totalInvoices : 0,
                                    nextCursor: nextCursor ?? null,
                                    hasMore: !!hasMoreInvoices,
                                  })
                                );
                              } catch {}

                              return merged;
                            });
                          } catch (err) {
                            console.error("❌ Status update failed:", err);

                            // rollback to previous row status (NOT fallback DRAFT unless that's truly what it was)
                            setInvoices((prev) =>
                              prev.map((x) => {
                                const k = String(x.id ?? x.invoiceNumber);
                                if (k !== rowKey) return x;
                                return { ...x, status: String(invoice.status || "DRAFT").toUpperCase() };
                              })
                            );

                            alert(t("alerts.status_update_failed"));
                          }
                        }}
                      />
                    </div>

                    {/* Ações por linha */}
                    <div
                      className="invoices-cell invoices-cell--actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Exporta PDF (download imediato) */}
                      <button
                        type="button"
                        className="invoices-icon-btn invoices-icon-btn--export"
                        title={t("actions.export_row")}
                        aria-label={t("actions.export_row")}
                        onClick={() => exportRowPdf(invoice)}
                      >
                        <FaDownload />
                      </button>

                      <button
                        type="button"
                        className="invoices-icon-btn invoices-icon-btn--email"
                        title={t("actions.send_email", { defaultValue: "Send by email" })}
                        aria-label={t("actions.send_email", { defaultValue: "Send by email" })}
                        onClick={() => handleSendInvoiceEmail(invoice)}
                        style={{
                          opacity:
                            sendingEmailId === (invoice.id ?? invoice.invoiceNumber)
                              ? 0.5
                              : 1,
                          pointerEvents:
                            sendingEmailId === (invoice.id ?? invoice.invoiceNumber)
                              ? "none"
                              : "auto",
                        }}
                      >
                        <FaPaperPlane />
                      </button>

                      <button
                        type="button"
                        className="invoices-icon-btn invoices-icon-btn--danger"
                        title={t("actions.delete")}
                        aria-label={t("actions.delete")}
                        onClick={() => handleDelete(invoice)}
                        style={{
                          opacity:
                            deletingId === (invoice.id ?? invoice.invoiceNumber)
                              ? 0.5
                              : 1,
                          pointerEvents:
                            deletingId === (invoice.id ?? invoice.invoiceNumber)
                              ? "none"
                              : "auto",
                        }}
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : loading && invoices.length === 0 ? null : (
              <div className="invoices-empty">
                <div className="invoices-empty__title">
                  {hasInvoiceSearchOrFilters && hasAnyInvoices
                    ? t("empty.no_results_title", {
                        defaultValue: "Nenhuma fatura encontrada",
                      })
                    : t("empty.title", {
                        defaultValue: "Ainda não existem faturas",
                      })}
                </div>
            
                <div className="invoices-empty__subtitle">
                  {hasInvoiceSearchOrFilters && hasAnyInvoices
                    ? t("empty.no_results_subtitle", {
                        defaultValue:
                          "Experimenta alterar a pesquisa ou limpar os filtros aplicados.",
                      })
                    : t("empty.subtitle", {
                        defaultValue:
                          "Cria a primeira fatura para começar a acompanhar cobranças, vencimentos e estados.",
                      })}
                </div>
            
                {hasInvoiceSearchOrFilters && hasAnyInvoices ? (
                  <button
                    className="invoices-btn invoices-btn--primary invoices-empty__button"
                    onClick={() => {
                      setInvoiceSearchTerm("");
                      setFilterValues({
                        status: "all",
                        dateFrom: "",
                        dateTo: "",
                        client: "",
                        pickup: "",
                        delivery: "",
                        minPrice: "",
                        maxPrice: "",
                      });
                    }}
                    type="button"
                  >
                    {t("empty.clear_filters", {
                      defaultValue: "Limpar pesquisa/filtros",
                    })}
                  </button>
                ) : (
                  <button
                    className="invoices-btn invoices-btn--primary invoices-empty__button"
                    onClick={() => navigate("/invoices/create")}
                    type="button"
                  >
                    {t("buttons.new_invoice", {
                      defaultValue: "+ New Invoice",
                    })}
                  </button>
                )}
              </div>
            )}
            <div ref={invoicesSentinelRef} style={{ height: 1 }} />

            {loadingMore && (
              <div className="invoices-loading" style={{ padding: "12px 0" }}>
                {t("loading")}
              </div>
            )}
          </div>
        </section>
      )}

      {/* === Componente invisível para gerar/printar === */}
      <div
        style={{
          position: "fixed",
          left: -10000, // fora do ecrã mas com dimensões reais
          top: 0,
          width: 794, // ~A4 @96dpi (bate com InvoicePrint.css)
          background: "#ffffff",
        }}
      >
        {printInvoice &&
          (() => {
            // 1) Preparar dados da fatura
            const prepared = {
              invoiceId:
                printInvoice.invoiceId ??
                printInvoice.invoiceNumber ??
                printInvoice.id,
            
              date: safeISO(printInvoice.issueDate ?? printInvoice.date),
              paymentDueDate: safeISO(printInvoice.paymentDueDate),
            
              status: printInvoice.status,
            
              pickup:
                printInvoice.pickup ??
                printInvoice.pickupLocation ??
                "—",
            
              delivery:
                printInvoice.delivery ??
                printInvoice.deliveryLocation ??
                "—",
            
              price: Number(printInvoice.price || 0),
            
              items: [
                {
                  description: `Transporte ${
                    printInvoice.pickup ?? printInvoice.pickupLocation ?? "—"
                  } → ${
                    printInvoice.delivery ?? printInvoice.deliveryLocation ?? "—"
                  }`,
                  qty: 1,
                  price: Number(printInvoice.price || 0),
                  total: Number(printInvoice.price || 0),
                },
              ],
            
              vatRate:
                printInvoice.vatRate ??
                printInvoice.taxRate ??
                printInvoice.iva ??
                null,
            
              taxRate:
                printInvoice.vatRate ??
                printInvoice.taxRate ??
                printInvoice.iva ??
                null,
            
              notes: "",
            };

            // 2) Cliente já resolvido em exportRowPdf (via loadId + clientType)
            const clientForPrint =
            printClient || {
              name: printInvoice.client || printInvoice.clientName || "",
              VAT_TaxInformation:
                printInvoice.VAT_TaxInformation ||
                printInvoice.vatTaxInformation ||
                printInvoice.vat_tax_id ||
                printInvoice.nif ||
                "",
            };

            // 3) Render do PDF bonitinho
            return (
              <InvoicePrint
                ref={printRef}
                invoice={prepared}
                issuer={printIssuer || companyForPrint}
                client={clientForPrint}
              />
            );
          })()}
      </div>

      {/* Modals */}
      <ExportInvoicesModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={doExport}
        defaultFrom={filterValues.dateFrom}
        defaultTo={filterValues.dateTo}
        count={filteredInvoices.filter((inv) => dateWithin(inv.date, filterValues.dateFrom, filterValues.dateTo)).length}
      />
      <FilterInvoicesModal
        open={isFilterModalOpen}
        value={filterValues}
        onChange={setFilterValues}
        clients={clientOptions}
        onApply={() => setIsFilterModalOpen(false)}
        onClear={() =>
          setFilterValues({
            status: "all",
            dateFrom: "",
            dateTo: "",
            client: "",
            pickup: "",
            delivery: "",
            minPrice: "",
            maxPrice: "",
          })
        }
        onClose={() => setIsFilterModalOpen(false)}
      />
    </div>
  );
}
